import React, {useEffect} from "react";
import {useAuth} from "react-oidc-context";
import {Route, Router, Switch} from 'react-router-dom'
import {createBrowserHistory} from 'history'
import WelcomePage from 'routes/WelcomePage'
import Dashboard from "routes/Dashboard.jsx";

import 'assets/scss/material-dashboard-pro-react.css?v=1.4.0'
import "react-vis/dist/style.css"
import PrivateRoute from "./components/PrivateRoute";
import {store} from "_helpers/store"
import {useDispatch} from "react-redux";
import {userActions} from "./_actions";
import { logger } from "./_helpers/logger";
const url = window.location.pathname + window.location.search
export const history = createBrowserHistory(url);

function App() {
  const auth = useAuth()
  const state = store.getState()
  const dispatch = useDispatch()

  // Sync user info to Redux on initial auth (for routing logic)
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      dispatch(userActions.updateUser(auth.user))
      
      // Log token expiry info (only on mount/auth change, not every user update)
      if (auth.user.expires_at) {
        const expiresAt = new Date(auth.user.expires_at * 1000);
        const now = new Date();
        const secondsUntilExpiry = Math.floor((auth.user.expires_at * 1000 - now.getTime()) / 1000);
        logger.debug('[Auth] Token expires at:', expiresAt);
        logger.debug('[Auth] Seconds until expiry:', secondsUntilExpiry);
        logger.debug('[Auth] Has refresh token:', !!auth.user.refresh_token);
      }
    }
  }, [auth.isAuthenticated, dispatch]); // Don't depend on auth.user to avoid re-runs

  // Listen for token expiry and renewal events
  useEffect(() => {
    if (!auth.events) return;

    const handleSilentRenewError = (error) => {
      logger.error('[Auth] Silent renewal failed:', error);
    };

    const handleAccessTokenExpiring = () => {
      logger.debug('[Auth] Access token is expiring, attempting renewal...');
    };

    const handleAccessTokenExpired = () => {
      logger.warn('[Auth] Access token has expired!');
    };

    const handleUserLoaded = (user) => {
      logger.debug('[Auth] User loaded/renewed:', !!user.refresh_token ? 'has refresh token' : 'no refresh token');
    };

    auth.events.addSilentRenewError(handleSilentRenewError);
    auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    auth.events.addAccessTokenExpired(handleAccessTokenExpired);
    auth.events.addUserLoaded(handleUserLoaded);

    return () => {
      auth.events.removeSilentRenewError(handleSilentRenewError);
      auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
      auth.events.removeUserLoaded(handleUserLoaded);
    };
  }, [auth.events]);

  // Simple manual token renewal - check every 10 minutes (runs during login and all times)
  useEffect(() => {
    logger.debug('Starting Effect for Checker')

    const checkAndRenew = async () => {
      logger.debug('[CHECK] Running Checker')
    
      // Get fresh user from localStorage directly
      const authority = `${window.REACT_APP_AUTHORITY}${window.REACT_APP_USER_POOL_ID}`;
      const clientId = window.REACT_APP_CLIENT_ID;
      const storageKey = `oidc.user:${authority}:${clientId}`;
      const userJson = localStorage.getItem(storageKey);
      
      if (!userJson) {
        logger.debug('[CHECK] No user found in storage, skipping check');
        return;
      }
      
      const currentUser = JSON.parse(userJson);
      if (!currentUser.expires_at) {
        logger.debug('[CHECK] User found in storage but no expiry info, skipping check');
        return;
      }
      
      const now = Math.floor(Date.now() / 1000);
      const expiresIn = currentUser.expires_at - now;
      
      // Renew if less than 5 minutes remaining (300 seconds), or if already expired
      if (expiresIn < 300) {
        logger.debug(`[Auth] Token expiring in ${expiresIn}s, renewing...`);
        try {
          await auth.signinSilent();
          logger.debug('[Auth] ✅ Token renewed successfully');
        } catch (error) {
          logger.error('[Auth] ❌ Token renewal failed:', error);
        }
      }
    };

    // Check immediately
    checkAndRenew();
    
    // Then check every 10 minutes (600000ms)
    logger.debug('[CHECK] Starting interval')
    const interval = setInterval(checkAndRenew, 600000);
    logger.debug('[CHECK] Done setting interval')
    
    return () => clearInterval(interval);
  }, [auth.isAuthenticated]);

  function WelcomePageWithState() {
    let loadingText = undefined;
    switch (auth.activeNavigator) {
      case "signinPopup":
        loadingText = "Signing you in in popup window..."
        break;
      default:
        break;
    }
    return (
      <WelcomePage loadingText={loadingText}/>
    )
  }

  if (state.authentication.user && auth.user) {
    return (<Router history={history}>
      <Switch>
        <Route exact path={"/"} component={WelcomePageWithState}/>
        <PrivateRoute exact component={Dashboard}/>
      </Switch>
    </Router>);
  }

  if (auth.isAuthenticated) {
    if (!state.authentication.user) {
      const newUser = auth.user
      dispatch(userActions.updateUser(newUser))
      history.push(url === "/" ? "/dashboard" : url)
    }
  }

  return (
    <Router history={history}>
      <Switch>
        <Route exact path={"/"} component={WelcomePageWithState}/>
        <PrivateRoute exact component={Dashboard}/>
      </Switch>
    </Router>);
}

export default App;
